import React from "react";
import { Link } from "@/helpers/router";
import S3Link from "@/components/common/links/s3-link";
import ResourceSearchLink from "@/components/common/links/resource-search-link";
import GooglePatentLink from "@/components/common/links/google-patent-link";
import UsptoPatentPdfLink from "@/components/common/links/uspto-patent-pdf-link";
import PriorArtLink from "@/components/common/links/prior-art-link";
import ReexamDocLink from "@/components/common/links/reexam-doc-link";
import { toString, isArray } from "lodash";

// signature of converter functions should be consistent with antd Table's `render`

export const toTitleCase = val => {
  const symbols = [
    // company suffixes
    "pllc",
    "p.l.l.c.",
    "plc",
    "p.l.c.",
    "llp",
    "l.l.p.",
    "pc",
    "p.c.",
    "llc",
    "llc.",
    "pa",
    "p.a.",
    "dla",
    "b.v.",
    "b.v",
    "s.a.",
    "s.a",
    "kg",
    // country
    "us",
    "usa",
    "u.s.",
    "u.s.a.",
    "us,",
    "usa,",
    "u.s.,",
    "u.s.a.,",
    // roman numerals
    "ii",
    "iii",
    "iv",
    "vi",
    "vii",
    "viii",
    "ix",
    // abbreviations
    "ibm",
    "hp",
    "lg",
    "ntt",
    "htc",
    "zte",
    "3d",
    "3m",
    "emc",
    "nec",
    "sap",
    "trw",
    "fmr",
    "ip",
    "npe",
    "ai"
  ];
  return val
    ? val
      .split(" ")
      .map(w => (symbols.indexOf(w) >= 0 || w.match(/[\d\w-]+\d+/i) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
      .join(" ")
      .split("-")
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join("-")
    : val;
};

export const toDateString = val => {
  return val ? new Date(val).toISOString().substr(0, 10) : "";
};

export const toClaimNumberString = val => {
  const shrinkClaimNumbers = cns => {
    let start,
      end,
      cnsNew = [];
    cns.forEach((cn, i) => {
      if (i === 0) {
        start = cn;
        end = cn;
      } else if (cn === end + 1) {
        end = cn;
      } else {
        cnsNew.push(start !== end ? `${start}-${end}` : `${end}`);
        start = cn;
        end = cn;
      }
    });
    cnsNew.push(start !== end ? `${start}-${end}` : `${end}`);
    return cnsNew;
  };
  return val && val !== "" ? shrinkClaimNumbers(val).join(", ") : val;
};

export const toSetString = val => {
  return isArray(val) && val.length
    ? val.map((v, i) => (
      <p style={{ margin: 0 }} key={i}>
        {v}
      </p>
    ))
    : val;
};

export const toItemPTABSearchLink = (val, record, index) => {
  return val ? <ResourceSearchLink resource="ptab" value={val} index={index} key={"ips"} /> : val;
};

export const toItemLitigationSearchLink = (val, record, index) => {
  return val ? <ResourceSearchLink resource="litigation" value={val} index={index} key={"ils"} /> : val;
};

export const toItemExparteSearchLink = (val, record, index) => {
  return val ? <ResourceSearchLink resource="exparte" value={val} index={index} key={"ies"} /> : val;
};

export const toCaseDetailLink = val => {
  return val && val !== "" ? (
    <Link to={`/ptab/case/${encodeURIComponent(val)}`} key={"cd"}>
      {val}
    </Link>
  ) : (
    val
  );
};

export const toJudgeDetailLink = (val, type) => {
  return val && val !== "" ? (
    <Link
      to={{
        pathname: `/ptab/judge/${encodeURIComponent(val)}`,
        query: type ? { type } : undefined
      }}
      key={"jd"}
    >
      {val}
    </Link>
  ) : (
    val
  );
};

// Patent numbers are either formatted ("US 10,123,456") or as UCIDs ("US-20130156099-A1"). Drop whitespace and commas,
// then drop a leading "US" only when a digit follows, so grant numbers resolve bare while UCIDs keep their country prefix.
export const normalizePatentNumber = val => {
  if (!val) return "";
  return String(val).replace(/\s+/g, "").replace(/,/g, "").replace(/^US(?=\d)/i, "");
};

export const toPatentDetailLink = val => {
  return val && val !== "" ? (
    <Link to={`/patents/patent/${encodeURIComponent(val)}`} key={"pd"}>
      {val}
    </Link>
  ) : (
    val
  );
};

export const toGooglePatentLink = val => {
  return val && val !== "" ? <GooglePatentLink value={val} key={"gp"} /> : val;
};

export const toUsptoPatentPdfLink = val => {
  return val && val !== "" ? <UsptoPatentPdfLink grantNumber={val} key={"upp"} /> : val;
};

export const toPriorArtLink = val => {
  return val && val !== "" ? <PriorArtLink value={val} key={"pa"} /> : val;
};

export const toFilingS3Link = (val, record) => {
  return record && record.pdf_id && record.pdf_id !== "" ? <S3Link fileKey={record.pdf_id} text={val} /> : <span>{val}</span>;
};

export const toReexamDocLink = (val, appNum, docId) => <ReexamDocLink text={val} applicationNumber={appNum} docId={docId} key={"rdl"} />;

export const toUPCDecisionOrderS3Link = (s3Key) => {
  const s3KeyWithoutExtension = s3Key ? s3Key.split("/")[1].replace(".pdf", "") : "";
  return s3Key && s3Key !== "" ? <S3Link fileKey={`upc-decisions-orders/${s3Key}`} text={s3KeyWithoutExtension} /> : <span>{s3KeyWithoutExtension}</span>;
};

export const toGenericLink = (val, link) => link ? <a target="_blank" rel="noopener noreferrer" href={`${link}`}>{val}</a> : <div>{val}</div>

export const toYesNo = val => {
  if (val === true) return "Yes";
  if (val === false) return "No";
  return val;
};

// One entry per line, trimmed, with the blank lines dropped.
const toSplittedLines = val => val
  ? toString(val).trim()
    .split(/\r?\n/)
    .map(e => e.trim())
    .filter(s => s.length > 0)
  : undefined;

// The same lines with the punctuation stripped out of each number, so 7,164,986 for example reaches a query as 7164986.
export const toSplittedNumbers = val => {
  const lines = toSplittedLines(val);
  return lines ? lines.map(e => e.replace(/[,.\s]/g, "")).filter(s => s.length > 0) : undefined;
};

// Country codes seen in publication numbers, the same list as the quick search patent number clause.
const PATENT_COUNTRIES = "US|CN|JP|DE|EP|KR|WO|GB|CA|AU|FR|TW|ES|RU|AT|IN|SU|IT|BR|SE";

// Reissue and comma-grouped numbers, which read the same with or without a country in front.
const PATENT_GROUPED = "RE\\d{2},?\\d{3}|\\d{1,2}(?:,\\d{3}){2}";

// Patent references in free text: a country-prefixed number (grant, reissue, publication, or
// year/serial) or a bare US grant number. The leading group rejects a "/", digit or letter boundary
// so PCT numbers (PCT/US...), application numbers (60/609,612) and design numbers (US-D1054752-S1)
// are left alone. Without the letter a design number would match on its digits alone and be read as
// the unrelated utility patent that shares them. The group also stands in for a lookbehind,
// which our Browserslist does not support.
const PATENT_RE = new RegExp(
  `(^|[^/\\dA-Za-z])((?:${PATENT_COUNTRIES})[- ]?(?:\\d{4}/\\d{6,8}|${PATENT_GROUPED}|\\d{6,11})(?:-?[A-Z]\\d?)?`
  + `|${PATENT_GROUPED}|\\d{7,8})(?![\\d/])`,
  "g"
);

// "US 2013/0156099" is the same document as US-20130156099-A1, written as a year over a 7 digit
// serial. A short serial is zero padded, which is lossless. Trimming an over-long one is a repair
// for known-bad source records (issue #2003 has a US2004/00833263) and guesses that the last digit
// is the stray one, so it is a workaround rather than a rule about the format.
const SLASH_PUBLICATION_RE = /^([A-Z]{2})(\d{4})\/(\d{6,8})$/;

// A part between two references holds the list together rather than saying anything, so it drops
// out once it has no letters or digits left. Separators clinging to real text are trimmed instead,
// narrowly, so brackets and quotes around a title survive.
const NO_ALNUM_RE = /^[^A-Za-z0-9]*$/;
const SEPARATOR_EDGE_RE = /^[\s,;.:|]+|[\s,;.:|]+$/g;

// A reference reduced to the contiguous number that identifies it.
const toPatentNumber = token => {
  const compact = token.toUpperCase().replace(/[,\s]/g, "");
  const slash = compact.match(SLASH_PUBLICATION_RE);
  if (!slash) return compact;
  const [, country, year, serial] = slash;
  return `${country}${year}${serial.padStart(7, "0").slice(0, 7)}`;
};

// References arrive as an array, or as one string that may carry HTML with <br /> separators, and
// come back as the non-empty lines. Clean text passes through unchanged, so this is safe to apply twice.
export const toReferenceLines = val =>
  (Array.isArray(val) ? val : [val]).flatMap(v =>
    String(v || "")
      .replace(/<\/?br\s*\/?>/gi, "\n")
      .replace(/<\/?[^>]+(>|$)/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .split("\n")
      .map(line => line.trim())
      .filter(Boolean));

// One line of text as its plain-text and reference segments. split() keeps both capture groups, so
// every third part (i % 3 === 2) is a matched reference and the rest is the text around it.
export const toReferenceSegments = line =>
  String(line || "")
    .split(PATENT_RE)
    .map((part, i) => (i % 3 === 2 ? { part, number: toPatentNumber(part) } : { part }));

// Every reference in a value as its own entry, with the numbers normalized. A separator between two
// references drops out, so a comma or semicolon separated line becomes one entry per reference. Text
// between references is kept rather than discarded, so nothing the user typed disappears.
export const collectPatentNumbers = val =>
  toReferenceLines(val)
    .flatMap(toReferenceSegments)
    .map(({ part, number }) => number || part.replace(SEPARATOR_EDGE_RE, ""))
    .filter(line => !NO_ALNUM_RE.test(line));

// Only the references in a value, normalized, with the text between them dropped. Callers that resolve
// numbers use this instead of collectPatentNumbers, since the surrounding text has nothing to resolve.
export const collectReferenceNumbers = val =>
  toReferenceLines(val)
    .flatMap(toReferenceSegments)
    .map(({ number }) => number)
    .filter(Boolean);

export const toUSDCurrency = val =>
  val.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

export const toReexamDetailLink = val => {
  return val && val !== "" ? (
    <Link to={`/exparte/${encodeURIComponent(val)}`} key={"re"}>
      {val}
    </Link>
  ) : (
    val
  );
};

export const toUPCDetailLink = val => {
  return val && val !== "" ? (
    <Link to={`/upc/case/${encodeURIComponent(val)}`} key={"upc-detail"}>
      {val}
    </Link>
  ) : (
    val
  );
};

export const toSetUPCDocumentLinkString = (titles, s3Key) => {
  return isArray(titles) && titles.length ? titles.map((doc, i) => (
    <div key={i}>
      {s3Key ? <S3Link fileKey={`upc-decisions-orders/${s3Key}`} text={doc} /> : <div>{doc}</div>}
    </div>
  )) : "N/A";
};

export const generateRandomString = (length = 8) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

const getPreambleFromText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return (text.split('\n')[0] || '').trim();
};

export const flattenClaims = (claims, parentIndex = '', startClaimNo = 1, parentPreamble = '') => {
  let currentClaimNo = startClaimNo;

  return claims.reduce((acc, claim, index) => {
    const key = parentIndex ? `${parentIndex}-${index}` : `${index}`;

    const claimNo = currentClaimNo;
    currentClaimNo++;

    const isIndependent = !parentIndex;
    const preamble = isIndependent
      ? (claim.preamble || getPreambleFromText(claim.text))
      : parentPreamble;

    acc.push({ key, text: claim.text, claimNo, preamble });

    if (claim.children && claim.children.length) {
      const childrenPreamble = isIndependent ? preamble : parentPreamble;
      const childrenResults = flattenClaims(claim.children, key, currentClaimNo, childrenPreamble);
      acc = acc.concat(childrenResults);
      currentClaimNo = childrenResults.length > 0
        ? childrenResults[childrenResults.length - 1].claimNo + 1
        : currentClaimNo;
    }
    return acc;
  }, []);
};
